import type { FilterOperator } from '@/types/query';

/** Operators offered for text-like columns in the ad-hoc "+ Filter" popover. */
export const TEXT_OPS: readonly { value: FilterOperator; label: string }[] = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '≠' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: '!contains' },
  { value: 'is_null', label: 'is empty' },
  { value: 'is_not_null', label: 'is not empty' },
];

/** Operators offered for numeric columns. */
export const NUMERIC_OPS: readonly { value: FilterOperator; label: string }[] = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '≠' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'between', label: 'between' },
  { value: 'is_null', label: 'is empty' },
  { value: 'is_not_null', label: 'is not empty' },
];

/** Operators offered for date/timestamp columns. */
export const DATE_OPS: readonly { value: FilterOperator; label: string }[] = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '≠' },
  { value: 'gt', label: 'after' },
  { value: 'gte', label: 'on or after' },
  { value: 'lt', label: 'before' },
  { value: 'lte', label: 'on or before' },
  { value: 'between', label: 'between' },
  { value: 'is_null', label: 'is empty' },
  { value: 'is_not_null', label: 'is not empty' },
];

/** Operators that take no right-hand value. */
export const NULLARY_OPS: ReadonlySet<FilterOperator> = new Set<FilterOperator>([
  'is_null',
  'is_not_null',
]);

export function isDateType(sqlType: string): boolean {
  const u = sqlType.toUpperCase();
  return u === 'DATE' || u.includes('TIMESTAMP');
}

export function isNumericType(sqlType: string): boolean {
  const u = sqlType.toUpperCase();
  return [
    'DOUBLE',
    'BIGINT',
    'INTEGER',
    'FLOAT',
    'DECIMAL',
    'REAL',
    'SMALLINT',
    'TINYINT',
    'HUGEINT',
  ].some((t) => u.includes(t));
}

export function isTextType(sqlType: string): boolean {
  const u = sqlType.toUpperCase();
  return u.includes('VARCHAR') || u === 'TEXT' || u === 'STRING';
}

/**
 * i18n key for an operator's user-visible label. The static `.label` on the
 * arrays above is kept as a non-translated fallback; UI callers should prefer
 * `t(opLabelKey(op))` so a TR build doesn't show "between" / "is empty".
 */
export function opLabelKey(op: FilterOperator): string {
  switch (op) {
    case 'is_null':
      return 'filters.op.isNull';
    case 'is_not_null':
      return 'filters.op.isNotNull';
    case 'not_in':
      return 'filters.op.notIn';
    case 'not_contains':
      return 'filters.op.notContains';
    default:
      return `filters.op.${op}`;
  }
}

/** Return the operator list appropriate for a SQL type. */
export function opsForType(
  sqlType: string,
): readonly { value: FilterOperator; label: string }[] {
  if (isDateType(sqlType)) return DATE_OPS;
  if (isNumericType(sqlType)) return NUMERIC_OPS;
  return TEXT_OPS;
}

/** Coarse category for UI dispatch. */
export type ColumnKind = 'text' | 'numeric' | 'date';

export function kindOf(sqlType: string): ColumnKind {
  if (isDateType(sqlType)) return 'date';
  if (isNumericType(sqlType)) return 'numeric';
  return 'text';
}

/** Parse a raw input string into the correct JS value for a column. */
export function parseValue(raw: string, sqlType: string): string | number {
  if (isNumericType(sqlType)) {
    const n = Number(raw);
    return Number.isNaN(n) ? raw : n;
  }
  return raw;
}
